import { useState, type ReactNode } from "react";
import { BLOOM_ID, isBotId } from "@bloom/shared";
import { useAvatar, useBotAvatar } from "../people.ts";
import { imgProps } from "../imageMemory.ts";

const PALETTE = ["#7C3AED", "#0E7490", "#B45309", "#2563EB", "#4D7C0F", "#9333EA", "#0F766E", "#BE185D"];

/** A stable color per person, from their id. */
export function colorFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

export function initials(name: string): string {
  return name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
}

export function Avatar({ id, name, size = 36, presence, image }: { id: string; name: string; size?: number; presence?: "online" | "offline"; image?: string | null }) {
  // @Bloom wears the Bloom logo wherever a person would have their avatar.
  if (id === BLOOM_ID) {
    return (
      <div className="avatar bloom-avatar" style={{ width: size, height: size }} aria-hidden="true">
        <BloomMark size={size} />
      </div>
    );
  }
  if (isBotId(id)) return <BotAvatar id={id} size={size} />;
  return <PersonAvatar id={id} name={name} size={size} presence={presence} image={image} />;
}

/** A bot wears its emoji (docs/bots.md), on its own colour like a person's initials. */
function BotAvatar({ id, size }: { id: string; size: number }) {
  const emoji = useBotAvatar(id);
  return (
    <div className="avatar bot-avatar" style={{ background: colorFor(id), width: size, height: size, fontSize: size * 0.55 }} aria-hidden="true">
      {emoji}
    </div>
  );
}

function PersonAvatar({ id, name, size, presence, image }: { id: string; name: string; size: number; presence?: "online" | "offline"; image?: string | null }) {
  const known = useAvatar(id);
  const src = image === undefined ? known : image;
  // A picture that fails to load (removed since, or offline) falls back to the initials.
  const [broken, setBroken] = useState<string | null>(null);
  const showImage = !!src && broken !== src;
  return (
    <div className={`avatar${showImage ? " has-image" : ""}`} style={{ background: colorFor(id), width: size, height: size, fontSize: size * 0.38 }} aria-hidden="true">
      {showImage ? <img src={src} alt="" width={size} height={size} {...imgProps(src)} onError={() => setBroken(src)} /> : initials(name)}
      {presence === "online" && <span className="presence" style={{ background: "var(--online)" }} />}
    </div>
  );
}

export function BloomMark({ size = 28 }: { size?: number }) {
  // The lotus on its own background (docs/brand/bloom-icon.png), rounded like the app icon.
  return <img className="bloom-mark" src="/bloom-mark.png" width={size} height={size} alt="" aria-hidden="true" style={{ borderRadius: size * 0.22 }} />;
}

export function AuthCard({ children }: { children: ReactNode }) {
  return (
    <main className="auth-page">
      <div className="auth-card">
        <div className="brand">
          <BloomMark /> Bloom
        </div>
        {children}
      </div>
    </main>
  );
}

export function Field(props: { id: string; label: string; type?: string; value: string; onChange: (v: string) => void; autoComplete?: string; autoFocus?: boolean; minLength?: number }) {
  return (
    <div className="field">
      <label htmlFor={props.id}>{props.label}</label>
      <input
        id={props.id}
        className="input"
        type={props.type ?? "text"}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        autoComplete={props.autoComplete}
        autoFocus={props.autoFocus}
        minLength={props.minLength}
        required
      />
    </div>
  );
}
